// How the rooms of a flat relate to each other: the rules every plan obeys
// by default, before any preference. They are the ones an architect checks
// first on someone else's plan, written down so the engine and the tests
// read the same list.
//
// 1. You enter a flat into the hall. Only a flat with no hall at all (a
//    studio opening straight into its living room) enters the living room.
// 2. A bathroom is never a way through. Nobody walks through the bath to
//    reach the living room, and nobody reaches a bedroom through the kitchen
//    or through another bedroom.
// 3. Each room is entered from the rooms listed for it below, preferring the
//    first: a bedroom from the hall, else from the living room; a bath from
//    the hall, and from the living room only where there is no hall.
// 4. The hall is circulation, not floor area for its own sake: at most
//    HALL_SHARE_MAX of the usable area (or HALL_FLOOR, whichever is more). A hall longer than its doors need
//    gives the rest to the living room it opens onto.
import type { RoomKind } from "./layout.ts";

/** For each room, the rooms it may be entered from, in order of preference. */
export const PASS_THROUGH: Record<RoomKind, RoomKind[]> = {
  hall: ["hall", "living"],
  living: ["hall"],
  kitchen: ["hall", "living"],
  bedroom: ["hall", "living"],
  bath: ["hall", "living", "bedroom", "storage"], // from a bedroom or its dressing: the en-suite of three rooms and up
  storage: ["hall", "living", "bedroom", "kitchen"],
};

/** The rooms a front door may open into, in order of preference. */
export const ENTRY: RoomKind[] = ["hall", "living"];

/** The hall's largest share of the flat's usable area. Romanian practice runs 8–12%. */
export const HALL_SHARE_MAX = 0.15;
/** A small flat still needs a hall to turn round in: the share applies above this. */
export const HALL_FLOOR = 6;
export const hallAllowance = (usable: number) => Math.max(HALL_FLOOR, HALL_SHARE_MAX * usable);
